import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Stack, TextField, InputAdornment, ToggleButton, ToggleButtonGroup, Box, alpha,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { admissionApplicationService } from '../../../services/admissions/admissionApplicationService';
import { ConcessionRequestType } from '../../../types/admissions';
import SearchableSelect from '../../common/SearchableSelect';
import { concessionValue } from '../../../utils/admissionFees';
import { useCurrencyFormat } from '../../../hooks/useCurrencyFormat';

interface Props {
  open: boolean;
  applicationId: string;
  /** What the applicant's admission challan totals (0 when no fee applies),
   *  for the live "what this takes off" preview. */
  admissionFeeTotal: number;
  onCancel: () => void;
  onAdded: () => Promise<void> | void;
}

type AppliesTo = 'tuition' | 'admission';

/**
 * Request a concession. It lands as Pending; someone with approval rights
 * (other than the requester) approves it from the Fees tab.
 *   - Monthly tuition → becomes a fee concession on every tuition challan
 *     from enrollment.
 *   - Admission fee → taken off the admission challan once (as a waiver).
 */

export const CONCESSION_TYPE_OPTIONS: Array<{ value: ConcessionRequestType; label: string; appliesTo: AppliesTo }> = [
  { value: 'sibling',     label: 'Sibling discount',          appliesTo: 'tuition' },
  { value: 'staff_child', label: 'Staff child',               appliesTo: 'tuition' },
  { value: 'merit',       label: 'Merit scholarship',         appliesTo: 'tuition' },
  { value: 'need_based',  label: 'Need-based',                appliesTo: 'tuition' },
  { value: 'hafiz',       label: 'Hafiz-e-Quran',             appliesTo: 'tuition' },
  { value: 'sports',      label: 'Sports / co-curricular',    appliesTo: 'tuition' },
  { value: 'orphan',      label: 'Orphan',                    appliesTo: 'tuition' },
  { value: 'shuhada',     label: 'Shuhada family',            appliesTo: 'tuition' },
  { value: 'disability',  label: 'Disability',                appliesTo: 'tuition' },
  { value: 'corporate',   label: 'Corporate / institutional', appliesTo: 'tuition' },
  { value: 'early_bird',  label: 'Early-bird',                appliesTo: 'admission' },
  { value: 'other',       label: 'Other',                     appliesTo: 'tuition' },
];

export const concessionTypeLabel = (t: ConcessionRequestType): string =>
  CONCESSION_TYPE_OPTIONS.find(o => o.value === t)?.label ?? t.replace(/_/g, ' ');

const ConcessionRequestDialog: React.FC<Props> = ({ open, applicationId, admissionFeeTotal, onCancel, onAdded }) => {
  const money = useCurrencyFormat();
  const { enqueueSnackbar } = useSnackbar();
  const [type, setType] = useState<ConcessionRequestType>('sibling');
  const [appliesTo, setAppliesTo] = useState<AppliesTo>('tuition');
  // A type suggests what it applies to, until the user picks that themselves.
  const [appliesToChosen, setAppliesToChosen] = useState(false);
  const [mode, setMode] = useState<'percent' | 'flat'>('percent');
  const [value, setValue] = useState('10');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const close = () => { if (!busy) onCancel(); };

  const changeType = (v: string) => {
    const opt = CONCESSION_TYPE_OPTIONS.find(o => o.value === v);
    if (!opt) return;
    setType(opt.value);
    if (!appliesToChosen) setAppliesTo(opt.appliesTo);
  };

  const num = Number(value);
  const valueError = !value.trim() || !Number.isFinite(num) || num <= 0
    ? 'Enter an amount above 0'
    : mode === 'percent' && num > 100
      ? 'A percentage can’t be more than 100'
      : mode === 'flat' && appliesTo === 'admission' && admissionFeeTotal > 0 && num > admissionFeeTotal
        ? `More than the admission fee (${money(admissionFeeTotal)})`
        : null;
  const canSubmit = !valueError && !!reason.trim();

  const preview = (() => {
    if (valueError) return null;
    if (appliesTo === 'admission') {
      if (admissionFeeTotal <= 0) return 'No admission fee applies to this class, so there is nothing to reduce.';
      const off = concessionValue({ mode, value: num }, admissionFeeTotal);
      return `${money(off)} off the ${money(admissionFeeTotal)} admission fee — payable ${money(admissionFeeTotal - off)}.`;
    }
    return mode === 'percent'
      ? `${num}% off every monthly tuition challan from enrollment.`
      : `${money(num)} off every monthly tuition challan from enrollment.`;
  })();

  const submit = async () => {
    setBusy(true);
    try {
      await admissionApplicationService.addConcessionRequest(applicationId, {
        type, mode, value: num, reason: reason.trim(), carryToFees: appliesTo === 'tuition',
      });
      enqueueSnackbar('Concession request added', { variant: 'success' });
      await onAdded();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to add concession', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle component="div" sx={{ pb: 0.5 }}>
        <Typography variant="h6" fontWeight={700}>Request a concession</Typography>
        <Typography variant="body2" color="text.secondary">
          Lands as pending until an approver signs it off.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 2 }}>
          <SearchableSelect
            label="Type"
            value={type}
            onChange={changeType}
            options={CONCESSION_TYPE_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
            clearable={false}
            fullWidth
            minWidth={0}
          />

          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.75 }}>
              Applies to
            </Typography>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={appliesTo}
              onChange={(_, v) => { if (v) { setAppliesTo(v); setAppliesToChosen(true); } }}
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, py: 0.9 } }}
            >
              <ToggleButton value="tuition">Monthly tuition</ToggleButton>
              <ToggleButton value="admission">Admission fee (one-time)</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <ToggleButtonGroup
              exclusive
              size="small"
              value={mode}
              onChange={(_, v) => v && setMode(v)}
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 1.75, height: 40 } }}
            >
              <ToggleButton value="percent">%</ToggleButton>
              <ToggleButton value="flat">Rs</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              size="small"
              label={mode === 'percent' ? 'Percentage' : 'Amount'}
              value={value}
              onChange={(e) => setValue(e.target.value.replace(/[^\d.]/g, ''))}
              inputProps={{ inputMode: 'decimal' }}
              InputProps={{
                endAdornment: mode === 'percent' ? <InputAdornment position="end">%</InputAdornment> : undefined,
                startAdornment: mode === 'flat' ? <InputAdornment position="start">Rs</InputAdornment> : undefined,
              }}
              error={!!valueError}
              helperText={valueError || ' '}
              sx={{ flex: 1 }}
            />
          </Stack>

          {preview && (
            <Box sx={{
              px: 1.75, py: 1.25, mt: -1, borderRadius: 2,
              bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
              color: 'text.primary',
            }}>
              <Typography variant="body2">{preview}</Typography>
            </Box>
          )}

          <TextField
            label="Reason"
            placeholder="E.g. Sibling in Grade 5 (GR 000012)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            multiline
            minRows={2}
            fullWidth
            size="small"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={close} disabled={busy}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={submit} disabled={busy || !canSubmit}>
          {busy ? 'Adding…' : 'Add request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConcessionRequestDialog;
